/**
 * Enable Banking API — Zod schemas and inferred types.
 *
 * GENERATED FILE — DO NOT EDIT BY HAND.
 * Regenerate with:
 *   node packages/shared/scripts/generate-enable-banking-schemas.mjs
 *
 * Source spec: https://enablebanking.com/docs/api/reference/enablebanking-api.yaml
 * Spec title:  API reference
 * Spec version (PINNED): 1.0.0-ef13dc17
 * Generated:   2026-09-06
 * Components:  ASPSPData, GetAspspsResponse, StartAuthorizationRequest, StartAuthorizationResponse, AuthorizeSessionResponse, GetSessionResponse, AccountResource, HalTransactions, Transaction, HalBalances, BalanceResource, BalanceStatus, TransactionStatus, ErrorResponse, ErrorCode (plus their transitive `$ref` closure).
 *
 * Generation policy — the three decisions the spec does not make for us:
 *
 * 1. NAMESPACED NAMES. Every symbol is prefixed (`ebTransactionSchema` /
 *    `EbTransaction`) because @releve/shared has one flat barrel and upstream
 *    names like `Transaction`, `Access`, `ErrorCode` and
 *    `CreditDebitIndicator` would collide with existing exports. In particular
 *    `ebCreditDebitIndicatorSchema` is Enable Banking's WIRE enum (CRDT/DBIT);
 *    the app's own indicator ('credit' | 'debit') stays
 *    `creditDebitIndicatorEnum` in ./clearingReads.ts and is NOT redeclared here.
 *
 * 2. OPTIONAL ⇒ `.nullish()`. OpenAPI 3.1 marks optionality only by absence from
 *    `required`, but the API returns explicit `null` for several optional fields
 *    (documented for `continuation_key`, `transaction_id`, `legal_age`).
 *    Accepting undefined AND null is the only read that survives both shapes.
 *
 * 3. NO `x-enum-descriptions` PAIRING and NO `default`s. The spec's
 *    `x-enum-descriptions` arrays are NOT positionally aligned with their `enum`
 *    arrays (compare ErrorCode's), so pairing them would emit false documentation.
 *    `default` is likewise dropped: applying a request-side default while parsing
 *    a RESPONSE would invent data the bank never sent.
 *
 * Formats (`date`, `date-time`, `uuid`, `uri`) are carried as JSDoc, not as Zod
 * refinements: a bank that returns a slightly off-spec uuid or timestamp must not
 * fail a whole page of transactions. `pattern`, `minLength` and `maxLength` ARE
 * enforced, because those are the constraints the mapper relies on.
 */
import { z } from "zod";
/**
 * Generated from the `ASPSP` component of the Enable Banking OpenAPI document.
 */
export const ebASPSPSchema = z.object({
    /**
     * Name of the ASPSP (i.e. a bank or a similar financial institution).
     */
    name: z.string(),
    /**
     * Two-letter ISO 3166 code of the country, in which ASPSP operates.
     */
    country: z.string(),
});
/**
 * Generated from the `PSUType` component of the Enable Banking OpenAPI document.
 */
export const ebPSUTypeSchema = z.enum(["business", "personal"]);
/**
 * Generated from the `Credential` component of the Enable Banking OpenAPI document.
 */
export const ebCredentialSchema = z.object({
    /**
     * Internal name of the credential. The name is to be used when passing credentials to
     * the "start user authorization" request.
     */
    name: z.string(),
    /**
     * Title for the credential to be displayed to PSU.
     */
    title: z.string(),
    /**
     * Indication whether the credential is required.
     */
    required: z.boolean(),
    /**
     * Description of the credential to be displayed to PSU.
     */
    description: z.string().nullish(),
    /**
     * Perl compatible regular expression used for check of the credential format.
     */
    template: z.string().nullish(),
});
/**
 * Generated from the `AuthenticationApproach` component of the Enable Banking OpenAPI
 * document.
 */
export const ebAuthenticationApproachSchema = z.enum(["REDIRECT", "DECOUPLED", "EMBEDDED"]);
/**
 * Generated from the `AuthMethod` component of the Enable Banking OpenAPI document.
 */
export const ebAuthMethodSchema = z.object({
    /**
     * Internal name of the authentication method.
     */
    name: z.string().nullish(),
    /**
     * Human-readable title of the authentication method.
     */
    title: z.string().nullish(),
    /**
     * PSU type to which the authentication method is applicable.
     */
    psu_type: ebPSUTypeSchema,
    /**
     * List of credentials which are possible to supply while initiating authorization.
     */
    credentials: z.array(ebCredentialSchema).nullish(),
    /**
     * Authentication approach used in the current authentication method.
     */
    approach: ebAuthenticationApproachSchema,
    /**
     * Flag showing whether the current authentication method is hidden from the user. If
     * `true` then the user will not be able to select this authentication method. It is
     * only possible to select this authentication method via API.
     */
    hidden_method: z.boolean(),
});
/**
 * Generated from the `SandboxUser` component of the Enable Banking OpenAPI document.
 */
export const ebSandboxUserSchema = z.object({
    /**
     * Username.
     */
    username: z.string().nullish(),
    /**
     * Password.
     */
    password: z.string().nullish(),
    /**
     * One time password.
     */
    otp: z.string().nullish(),
});
/**
 * Generated from the `SandboxInfo` component of the Enable Banking OpenAPI document.
 */
export const ebSandboxInfoSchema = z.object({
    /**
     * List of sandbox users which can be used to test sandbox environment.
     */
    users: z.array(ebSandboxUserSchema).nullish(),
});
/**
 * Generated from the `PaymentType` component of the Enable Banking OpenAPI document.
 */
export const ebPaymentTypeSchema = z.enum(["SEPA", "INST_SEPA", "DOMESTIC", "CROSSBORDER", "DOMESTIC_PL_ELIXIR", "DOMESTIC_PL_EXPRESS_ELIXIR", "DOMESTIC_PL_SORBNET", "DOMESTIC_PL_BLUE_CASH", "DOMESTIC_SE_GIRO", "INTERNAL", "BULK_SEPA", "BULK_DOMESTIC", "BULK_DOMESTIC_SE_GIRO"]);
/**
 * Generated from the `SchemeName` component of the Enable Banking OpenAPI document.
 */
export const ebSchemeNameSchema = z.enum(["CHID", "GS1G", "DUNS", "BANK", "TXID", "CUST", "EMPL", "OTHC", "DRLC", "CUSI", "SOSE", "ARNU", "CCPT", "OTHI", "COID", "SREN", "SRET", "NIDN", "OAUT", "CPAN", "BBAN", "IBAN", "MIBN", "BGNR", "PGNR"]);
/**
 * Generated from the `PriorityCode` component of the Enable Banking OpenAPI document.
 */
export const ebPriorityCodeSchema = z.enum(["HIGH", "NORM", "EXPR"]);
/**
 * Generated from the `ChargeBearerCode` component of the Enable Banking OpenAPI
 * document.
 */
export const ebChargeBearerCodeSchema = z.enum(["SLEV", "SHAR", "DEBT", "CRED"]);
/**
 * Generated from the `RemittanceInformationLineInfo` component of the Enable Banking
 * OpenAPI document.
 */
export const ebRemittanceInformationLineInfoSchema = z.object({
    /**
     * Minimum length of remittance information line.
     */
    min_length: z.number().int().nullish(),
    /**
     * Maximum length of remittance information line.
     */
    max_length: z.number().int().nullish(),
    /**
     * Specifies a regexp pattern for the remittance information line.
     */
    pattern: z.string().nullish(),
});
/**
 * Generated from the `RegulatoryReportingCode` component of the Enable Banking OpenAPI
 * document.
 */
export const ebRegulatoryReportingCodeSchema = z.object({
    /**
     * Value of the code, i.e. what needs to be passed as a code when filling in regulatory
     * reporting details.
     */
    value: z.string(),
    /**
     * Regulatory authority to which reporting shall be made.
     */
    description: z.string(),
});
/**
 * Generated from the `ReferenceNumberScheme` component of the Enable Banking OpenAPI
 * document.
 */
export const ebReferenceNumberSchemeSchema = z.enum(["SEBG", "NORF", "FIRF", "INTL", "BERF", "SDDM"]);
/**
 * Generated from the `PaymentStatus` component of the Enable Banking OpenAPI document.
 */
export const ebPaymentStatusSchema = z.enum(["ACCC", "ACCP", "ACSC", "ACSP", "ACTC", "ACWC", "ACWP", "PART", "RCVD", "PDNG", "RJCT", "ACPT", "ACCR", "RJCR", "PACR", "PDCR", "CNCL", "NULL"]);
/**
 * Generated from the `ResponsePaymentType` component of the Enable Banking OpenAPI
 * document.
 */
export const ebResponsePaymentTypeSchema = z.object({
    /**
     * Payment type name.
     */
    payment_type: ebPaymentTypeSchema,
    /**
     * Maximum number of transactions per payment.
     */
    max_transactions: z.number().int().nullish(),
    /**
     * List of supported currencies.
     */
    currencies: z.array(z.string()).nullish(),
    /**
     * States if debtor account is required for payment initiation request. When the debtor
     * account is not provided it is to be chosen by the PSU during payment authorisation
     * flow. Create payment body field: `payment_request.debtor_account`.
     */
    debtor_account_required: z.boolean().nullish(),
    /**
     * List of supported debtor account schemas.
     */
    debtor_account_schemas: z.array(ebSchemeNameSchema).nullish(),
    /**
     * List of supported creditor account schemas.
     */
    creditor_account_schemas: z.array(ebSchemeNameSchema).nullish(),
    /**
     * List of supported priority codes.
     */
    priority_codes: z.array(ebPriorityCodeSchema).nullish(),
    /**
     * List of supported charge bearer codes.
     */
    charge_bearer_values: z.array(ebChargeBearerCodeSchema).nullish(),
    /**
     * States if creditor country is required. Create payment body field:
     * `payment_request.credit_transfer_transaction[*].beneficiary.creditor.postal_address.country`.
     */
    creditor_country_required: z.boolean().nullish(),
    /**
     * States if creditor name is required. Create payment body field:
     * `payment_request.credit_transfer_transaction[*].beneficiary.creditor.postal_address.country`.
     */
    creditor_name_required: z.boolean().nullish(),
    /**
     * States if creditor postal address is required. Create payment body field:
     * `payment_request.credit_transfer_transaction[*].beneficiary.creditor.postal_address`.
     */
    creditor_postal_address_required: z.boolean().nullish(),
    /**
     * States if remittance information is required. Create payment body field:
     * `payment_request.credit_transfer_transaction[*].remittance_information`.
     */
    remittance_information_required: z.boolean().nullish(),
    /**
     * Properties of remittance information. Each item of the array correspond to the
     * remittance information line with the same index. When provided, the number of lines
     * in the remittance information should be the same as the length of this array.
     */
    remittance_information_lines: z.array(ebRemittanceInformationLineInfoSchema).nullish(),
    /**
     * States if debtor currency is required. Create payment body field:
     * `payment_request.debtor_currency`.
     */
    debtor_currency_required: z.boolean().nullish(),
    /**
     * States if debtor's contact email is required when a payment this type is being
     * initiated. Create payment body field:
     * `payment_request.debtor.contact_details.email_address`.
     */
    debtor_contact_email_required: z.boolean().nullish(),
    /**
     * States if debtor's contact phone is required when a payment this type is being
     * initiated. Create payment body field:
     * `payment_request.debtor.contact_details.phone_number`.
     */
    debtor_contact_phone_required: z.boolean().nullish(),
    /**
     * States if creditor agent bicFi is required. Create payment body field:
     * `payment_request.credit_transfer_transaction[*].beneficiary.creditor_agent.bic_fi`.
     */
    creditor_agent_bic_fi_required: z.boolean().nullish(),
    /**
     * States if creditor agent clearing system member ID is required. Create payment body
     * field:
     * `payment_request.credit_transfer_transaction[*].beneficiary.creditor_agent.clearing_system_member_id`.
     */
    creditor_agent_clearing_system_member_id_required: z.boolean().nullish(),
    /**
     * List of supported auth methods for this payment type.
     */
    allowed_auth_methods: z.array(z.string()).nullish(),
    /**
     * List of supported codes for regulatory reporting details.
     */
    regulatory_reporting_codes: z.array(ebRegulatoryReportingCodeSchema).nullish(),
    /**
     * States if regulatory reporting shall be provided for credit transfer transactions.
     * Create payment body field:
     * `payment_request.credit_transfer_transaction[*].regulatory_reporting.details.code`.
     */
    regulatory_reporting_code_required: z.boolean().nullish(),
    /**
     * States if reference number can be provided for credit transfer transactions.
     */
    reference_number_supported: z.boolean().nullish(),
    /**
     * List of reference number schemas supported by a payment method.
     */
    reference_number_schemas: z.array(ebReferenceNumberSchemeSchema).nullish(),
    /**
     * States if requested execution date supported by a payment method.
     */
    requested_execution_date_supported: z.boolean().nullish(),
    /**
     * Maximum requested execution date interval in the future.
     */
    requested_execution_date_max_period: z.number().int().nullish(),
    /**
     * States if both reference number and remittance information can be provided
     * simultaneously.
     */
    remittance_reference_supported: z.boolean().nullish(),
    /**
     * Indicates whether this payment type supports deferred submission for execution. When
     * true, the automatic submission of the payment for execution after PSU authorization
     * can be deferred — the client must call POST /payments/{payment_id}/submit to
     * explicitly submit the payment for execution.
     */
    deferred_submission_supported: z.boolean().nullish(),
    /**
     * List of the final successful statuses for the payment type. Please note that when a
     * payment reaches one of the statuses provided in this list, it is not yet guaranteed
     * that the funds will be credited to the creditor's account specified in the payment
     * request. It is up to the application that created the payment to conclude whether
     * the funds are (or will be) received, depending on the actual payment status and
     * other factors.
     */
    final_successful_statuses: z.array(ebPaymentStatusSchema).nullish(),
    /**
     * PSU type.
     */
    psu_type: ebPSUTypeSchema,
});
/**
 * Generated from the `ASPSPGroup` component of the Enable Banking OpenAPI document.
 */
export const ebASPSPGroupSchema = z.object({
    /**
     * Name of the group, which the ASPSP belongs to.
     */
    name: z.string(),
    /**
     * URL of the logo for the group to which the ASPSP belongs. This URL supports the same
     * transformation postfixes as ASPSP logo URLs. Spec format: uri.
     */
    logo: z.string().min(1),
});
/**
 * Generated from the `ASPSPData` component of the Enable Banking OpenAPI document.
 */
export const ebASPSPDataSchema = z.object({
    /**
     * Name of the ASPSP (i.e. a bank or a similar financial institution).
     */
    name: z.string(),
    /**
     * Two-letter ISO 3166 code of the country, in which ASPSP operates.
     */
    country: z.string(),
    /**
     * ASPSP logo URL. It is possible to transform (e.g. resize) the logo by adding special
     * suffixes at the end of the URL. For example, `-/resize/500x/`. For full list of
     * possible transformations, please refer to
     * https://uploadcare.com/docs/transformations/image/. Spec format: uri.
     */
    logo: z.string().min(1).max(2083),
    /**
     * List of PSU types supported by ASPSP.
     */
    psu_types: z.array(ebPSUTypeSchema),
    /**
     * List of available authentication methods. Provided in case multiple methods are
     * available or it is possible to supply authentication credentials while initiating
     * authorization.
     */
    auth_methods: z.array(ebAuthMethodSchema),
    /**
     * Maximum consent validity which bank supports in seconds.
     */
    maximum_consent_validity: z.number().int(),
    /**
     * Applicable only to sandbox environment. Additional information necessary to use
     * sandbox environment.
     */
    sandbox: ebSandboxInfoSchema.nullish(),
    /**
     * Flag showing whether implementation is in beta mode.
     */
    beta: z.boolean(),
    /**
     * BIC of the ASPSP.
     */
    bic: z.string().nullish(),
    /**
     * List of the headers required to indicate to data retrieval endpoints that PSU is
     * online. Either all required PSU headers or none of PSU headers are to be provided,
     * otherwise PSU_HEADER_NOT_PROVIDED error will be returned.
     */
    required_psu_headers: z.array(z.string()).nullish(),
    /**
     * Supported payment types by country and their properties.
     */
    payments: z.array(ebResponsePaymentTypeSchema).nullish(),
    /**
     * Group, which the ASPSP belongs to.
     */
    group: ebASPSPGroupSchema.nullish(),
});
/**
 * Generated from the `GenericIdentification` component of the Enable Banking OpenAPI
 * document.
 */
export const ebGenericIdentificationSchema = z.object({
    /**
     * An identifier.
     */
    identification: z.string(),
    /**
     * Name of the identification scheme. Partially based on ISO20022 external code list.
     */
    scheme_name: ebSchemeNameSchema,
    /**
     * Entity that assigns the identification. This could be a country code or any
     * organisation name or identifier that can be recognized by both parties.
     */
    issuer: z.string().nullish(),
});
/**
 * Generated from the `AccountIdentification` component of the Enable Banking OpenAPI
 * document.
 */
export const ebAccountIdentificationSchema = z.object({
    /**
     * International Bank Account Number (IBAN) - identification used internationally by
     * financial institutions to uniquely identify the account of a customer. Further
     * specifications of the format and content of the IBAN can be found in the standard
     * ISO 13616 "Banking and related financial services - International Bank Account
     * Number (IBAN)" version 1997-10-01, or later revisions.
     */
    iban: z.string().nullish(),
    /**
     * Other identification if iban is not provided.
     */
    other: ebGenericIdentificationSchema.nullish(),
});
/**
 * Generated from the `Access` component of the Enable Banking OpenAPI document.
 */
export const ebAccessSchema = z.object({
    /**
     * List of accounts access to which is requested. If not set behaviour depends on the
     * bank: some banks allow users to choose list of accessible accounts through their
     * access consent UI, while other may provide access to all accounts or just access to
     * the list of accounts.
     */
    accounts: z.array(ebAccountIdentificationSchema).nullish(),
    /**
     * Request consent with balances access.
     */
    balances: z.boolean().nullish(),
    /**
     * Request consent with transactions access.
     */
    transactions: z.boolean().nullish(),
    /**
     * This parameter specifies the date and time until which the authorised session
     * remains valid. The value must be in the RFC3339 date-time format with a timezone
     * offset, e.g. `2025-12-01T12:00:00.000000+00:00`. The provided value cannot exceed
     * the date and time, calculated as "now" + `maximum_consent_validity` (provided in
     * seconds for each ASPSP in response to the GET /aspsps request). The provided value
     * is subject to adjustment to comply with the ASPSP's requirements. Specifically, if
     * the provided value is less than the minimum consent validity allowed by the ASPSP
     * (e.g., some ASPSPs require a minimum of 1 hour or 1 day), the consent validity will
     * be adjusted to meet these requirements. However, the session validity will remain
     * exactly as specified. This means that even if the consent remains valid on the
     * ASPSP's side, the session will expire based on the initially provided value. Spec
     * format: date-time.
     */
    valid_until: z.string(),
});
/**
 * Generated from the `ClearingSystemMemberIdentification` component of the Enable
 * Banking OpenAPI document.
 */
export const ebClearingSystemMemberIdentificationSchema = z.object({
    /**
     * Specification of a pre-agreed offering between clearing agents or the channel
     * through which the payment instruction is processed.
     */
    clearing_system_id: z.string().nullish(),
    /**
     * Identification of a member of a clearing system.
     */
    member_id: z.string().nullish(),
});
/**
 * Generated from the `FinancialInstitutionIdentification` component of the Enable
 * Banking OpenAPI document.
 */
export const ebFinancialInstitutionIdentificationSchema = z.object({
    /**
     * Code allocated to a financial institution by the ISO 9362 Registration Authority as
     * described in ISO 9362 "Banking - Banking telecommunication messages - Business
     * identification code (BIC)".
     */
    bic_fi: z.string().nullish(),
    /**
     * Information used to identify a member within a clearing system.
     */
    clearing_system_member_id: ebClearingSystemMemberIdentificationSchema.nullish(),
    /**
     * Name of the financial institution.
     */
    name: z.string().nullish(),
});
/**
 * Generated from the `Usage` component of the Enable Banking OpenAPI document.
 */
export const ebUsageSchema = z.enum(["PRIV", "ORGA"]);
/**
 * Generated from the `CashAccountType` component of the Enable Banking OpenAPI
 * document.
 */
export const ebCashAccountTypeSchema = z.enum(["CACC", "CASH", "CARD", "LOAN", "SVGS", "OTHR"]);
/**
 * Generated from the `AmountType` component of the Enable Banking OpenAPI document.
 */
export const ebAmountTypeSchema = z.object({
    /**
     * ISO 4217 code of the currency of the amount.
     */
    currency: z.string(),
    /**
     * Numerical value or monetary figure associated with a particular transaction,
     * representing balance on an account, a fee or similar. Represented as a decimal
     * number, using . (dot) as a decimal separator. Allowed precision (number of digits
     * after the decimal separator) varies depending on the currency and is validated
     * differently depending on the context.
     */
    amount: z.string().regex(/^-?\d+(\.\d+)?$/),
});
/**
 * Generated from the `AddressType` component of the Enable Banking OpenAPI document.
 */
export const ebAddressTypeSchema = z.enum(["Business", "Correspondence", "DeliveryTo", "MailTo", "POBox", "Postal", "Residential", "Statement"]);
/**
 * Generated from the `PostalAddress` component of the Enable Banking OpenAPI document.
 */
export const ebPostalAddressSchema = z.object({
    /**
     * Available address type values.
     */
    address_type: ebAddressTypeSchema.nullish(),
    /**
     * Identification of a division of a large organisation or building.
     */
    department: z.string().nullish(),
    /**
     * Identification of a sub-division of a large organisation or building.
     */
    sub_department: z.string().nullish(),
    /**
     * Name of a street or thoroughfare.
     */
    street_name: z.string().nullish(),
    /**
     * Number that identifies the position of a building on a street.
     */
    building_number: z.string().nullish(),
    /**
     * Identifier consisting of a group of letters and/or numbers that is added to a postal
     * address to assist the sorting of mail.
     */
    post_code: z.string().nullish(),
    /**
     * Name of a built-up area, with defined boundaries, and a local government.
     */
    town_name: z.string().nullish(),
    /**
     * Identifies a subdivision of a country such as state, region, county.
     */
    country_sub_division: z.string().nullish(),
    /**
     * Two-letter ISO 3166 code of the country in which a person resides (the place of a
     * person's home). In the case of a company, it is the country from which the affairs
     * of that company are directed.
     */
    country: z.string().nullish(),
    /**
     * Unstructured address. The two lines must embed zip code and town name.
     */
    address_line: z.array(z.string()).nullish(),
});
/**
 * Generated from the `AccountResource` component of the Enable Banking OpenAPI
 * document.
 */
export const ebAccountResourceSchema = z.object({
    /**
     * Primary account identifier.
     */
    account_id: ebAccountIdentificationSchema.nullish(),
    /**
     * All account identifiers provided by ASPSPs (including primary identifier available
     * in the accountId field).
     */
    all_account_ids: z.array(ebGenericIdentificationSchema).nullish(),
    /**
     * Information about the financial institution servicing the account.
     */
    account_servicer: ebFinancialInstitutionIdentificationSchema.nullish(),
    /**
     * Account holder(s) name.
     */
    name: z.string().nullish(),
    /**
     * Account description set by PSU or provided by ASPSP.
     */
    details: z.string().nullish(),
    /**
     * Specifies the usage of the account.
     */
    usage: ebUsageSchema.nullish(),
    /**
     * Specifies the type of the account.
     */
    cash_account_type: ebCashAccountTypeSchema,
    /**
     * Product Name of the Bank for this account, proprietary definition.
     */
    product: z.string().nullish(),
    /**
     * Specifies the currency of the account.
     */
    currency: z.string(),
    /**
     * Relationship between the PSU and the account - Account Holder - Co-account Holder -
     * Attorney.
     */
    psu_status: z.string().nullish(),
    /**
     * Specifies the maximum credit or overdraft allowed on the account.
     */
    credit_limit: ebAmountTypeSchema.nullish(),
    /**
     * Specifies whether Enable Banking is confident that the account holder is of legal
     * age or is a minor. The field takes the following values: true if the account holder
     * is of legal age; false if the account holder is a minor; null (or the field is not
     * set) if it is not possible to determine whether the account holder is of legal age
     * or a minor or if the legal age check is not applicable (in cases such as if the
     * account holder is a legal entity or there are multiple account co-holders).
     */
    legal_age: z.boolean().nullish(),
    /**
     * Postal address of the account holder.
     */
    postal_address: ebPostalAddressSchema.nullish(),
    /**
     * Unique account identificator used for fetching account balances and transactions. It
     * is valid only until the session to which the account belongs is in the AUTHORIZED
     * status. It can be not set in case it is know that it is not possible to fetch
     * balances and transactions for the account (for example, in case the account is
     * blocked or closed at the ASPSP side). Spec format: uuid.
     */
    uid: z.string().nullish(),
    /**
     * Primary account identification hash. It can be used for matching accounts between
     * multiple sessions (even in case the sessions are authorized by different PSUs).
     */
    identification_hash: z.string(),
    /**
     * List of possible account identification hashes. Identification hash is based on the
     * account number. Some accounts may have multiple account numbers (e.g. IBAN and
     * BBAN). This field contains all possible hashes. Not all of these hashes can be used
     * to uniquely identify an account and that the primary goal of them is to be able to
     * fuzzy matching of accounts by certain properties. Primary hash is included in this
     * list.
     */
    identification_hashes: z.array(z.string()),
});
/**
 * Generated from the `AuthorizeSessionResponse` component of the Enable Banking
 * OpenAPI document.
 */
export const ebAuthorizeSessionResponseSchema = z.object({
    /**
     * ID of the PSU session. Spec format: uuid4.
     */
    session_id: z.string(),
    /**
     * List of authorized accounts.
     */
    accounts: z.array(ebAccountResourceSchema),
    /**
     * ASPSP used with the session.
     */
    aspsp: ebASPSPSchema,
    /**
     * PSU type used with the session.
     */
    psu_type: ebPSUTypeSchema,
    /**
     * Scope of access requested from ASPSP and confirmed by PSU.
     */
    access: ebAccessSchema,
});
/**
 * Generated from the `BalanceStatus` component of the Enable Banking OpenAPI document.
 */
export const ebBalanceStatusSchema = z.enum(["CLAV", "CLBD", "FWAV", "INFO", "ITAV", "ITBD", "OPAV", "OPBD", "PRCD", "OTHR", "VALU", "XPCD"]);
/**
 * Generated from the `BalanceResource` component of the Enable Banking OpenAPI
 * document.
 */
export const ebBalanceResourceSchema = z.object({
    /**
     * Label of the balance.
     */
    name: z.string(),
    /**
     * Structure aiming to embed the amount and the currency to be used.
     */
    balance_amount: ebAmountTypeSchema,
    /**
     * Available balance type values.
     */
    balance_type: ebBalanceStatusSchema,
    /**
     * Timestamp of the last change of the balance amount. Spec format: date-time.
     */
    last_change_date_time: z.string().nullish(),
    /**
     * Reference date for the balance. Spec format: date.
     */
    reference_date: z.string().nullish(),
    /**
     * Entry reference of the last transaction contributing to the balance value.
     */
    last_committed_transaction: z.string().nullish(),
});
/**
 * Generated from the `BankTransactionCode` component of the Enable Banking OpenAPI
 * document.
 */
export const ebBankTransactionCodeSchema = z.object({
    /**
     * Arbitrary transaction categorization description.
     */
    description: z.string().nullish(),
    /**
     * Specifies the family of a transaction within the domain.
     */
    code: z.string().nullish(),
    /**
     * Specifies the sub-product family of a transaction within a specific family.
     */
    sub_code: z.string().nullish(),
});
/**
 * Generated from the `ContactDetails` component of the Enable Banking OpenAPI
 * document.
 */
export const ebContactDetailsSchema = z.object({
    /**
     * Email address of a person.
     */
    email_address: z.string().nullish(),
    /**
     * Phone number of a person.
     */
    phone_number: z.string().nullish(),
});
/**
 * Generated from the `CreditDebitIndicator` component of the Enable Banking OpenAPI
 * document.
 */
export const ebCreditDebitIndicatorSchema = z.enum(["CRDT", "DBIT"]);
/**
 * Specifies the currency of the amount or of the account according the ISO 4217
 * standard Generated from the `CurrencyCode` component of the Enable Banking OpenAPI
 * document.
 */
export const ebCurrencyCodeSchema = z.string();
/**
 * Generated from the `ErrorCode` component of the Enable Banking OpenAPI document.
 */
export const ebErrorCodeSchema = z.enum(["ACCESS_DENIED", "ACCOUNT_DOES_NOT_EXIST", "ALREADY_AUTHORIZED", "ASPSP_ACCOUNT_NOT_ACCESSIBLE", "ASPSP_ERROR", "ASPSP_TIMEOUT", "ASPSP_RATE_LIMIT_EXCEEDED", "AUTHORIZATION_NOT_PROVIDED", "CLOSED_SESSION", "DATE_TO_WITHOUT_DATE_FROM", "DATE_FROM_IN_FUTURE", "EXPIRED_AUTHORIZATION_CODE", "EXPIRED_SESSION", "INVALID_ACCOUNT_ID", "INVALID_HOST", "UNAUTHORIZED_IP", "NO_ACCOUNTS_ADDED", "PAYMENT_NOT_FOUND", "PSU_HEADER_NOT_PROVIDED", "PSU_HEADER_INVALID", "REDIRECT_URI_NOT_ALLOWED", "REVOKED_SESSION", "SESSION_DOES_NOT_EXIST", "UNAUTHORIZED_ACCESS", "UNTRUSTED_PAYMENT_PARTY", "WEBHOOK_URI_NOT_ALLOWED", "WRONG_ASPSP_PROVIDED", "WRONG_AUTHORIZATION_CODE", "WRONG_DATE_INTERVAL", "WRONG_CREDENTIALS_PROVIDED", "WRONG_REQUEST_PARAMETERS", "WRONG_SESSION_STATUS", "WRONG_TRANSACTIONS_PERIOD", "WRONG_CONTINUATION_KEY", "TRANSACTION_DOES_NOT_EXIST", "PAYMENT_LIMIT_EXCEEDED", "ASPSP_PAYMENT_NOT_ACCESSIBLE", "INVALID_PAYMENT", "ASPSP_PSU_ACTION_REQUIRED", "PAYMENT_NOT_FINALIZED", "PAYMENT_SUBMISSION_NOT_SUPPORTED", "PAYMENT_NOT_AUTHORIZED", "PAYMENT_SUBMISSION_NOT_DEFERRED"]);
/**
 * Generated from the `ErrorResponse` component of the Enable Banking OpenAPI document.
 */
export const ebErrorResponseSchema = z.object({
    /**
     * Error message.
     */
    message: z.string(),
    /**
     * Error code, identical to the http response code.
     */
    code: z.number().int().nullish(),
    /**
     * Text error code.
     */
    error: ebErrorCodeSchema.nullish(),
    /**
     * Detailed explanation of an error.
     */
    detail: z.unknown().nullish(),
});
/**
 * Generated from the `RateType` component of the Enable Banking OpenAPI document.
 */
export const ebRateTypeSchema = z.enum(["SPOT", "SALE", "AGRD"]);
/**
 * Generated from the `ExchangeRate` component of the Enable Banking OpenAPI document.
 */
export const ebExchangeRateSchema = z.object({
    /**
     * ISO 4217 code of the currency, in which the rate of exchange is expressed in a
     * currency exchange. In the example 1GBP = xxxCUR, the unit currency is GBP.
     */
    unit_currency: ebCurrencyCodeSchema.nullish(),
    /**
     * The factor used for conversion of an amount from one currency to another. This
     * reflects the price at which one currency was bought with another currency.
     */
    exchange_rate: z.string().nullish(),
    /**
     * Specifies the type of exchange rate applied to the transaction.
     */
    rate_type: ebRateTypeSchema.nullish(),
    /**
     * Unique and unambiguous reference to the foreign exchange contract agreed between the
     * initiating party/creditor and the debtor agent.
     */
    contract_identification: z.string().nullish(),
    /**
     * Original amount, in which transaction was initiated. In particular, for
     * cross-currency card transactions, the value represents original value of a purchase
     * or a withdrawal in a currency different from the card's native or default currency.
     */
    instructed_amount: ebAmountTypeSchema.nullish(),
});
/**
 * Generated from the `GetAspspsResponse` component of the Enable Banking OpenAPI
 * document.
 */
export const ebGetAspspsResponseSchema = z.object({
    /**
     * List of available ASPSPs and countries.
     */
    aspsps: z.array(ebASPSPDataSchema),
});
/**
 * Generated from the `SessionStatus` component of the Enable Banking OpenAPI document.
 */
export const ebSessionStatusSchema = z.enum(["INVALID", "PENDING_AUTHORIZATION", "RETURNED_FROM_BANK", "AUTHORIZED", "EXPIRED", "CLOSED", "REVOKED", "CANCELLED"]);
/**
 * Generated from the `SessionAccount` component of the Enable Banking OpenAPI
 * document.
 */
export const ebSessionAccountSchema = z.object({
    /**
     * Account identificator within the session. Spec format: uuid.
     */
    uid: z.string(),
    /**
     * Global account identification hash.
     */
    identification_hash: z.string(),
    /**
     * List of possible account identification hashes. Identification hash is based on the
     * account number. Some accounts may have multiple account numbers (e.g. IBAN and
     * BBAN). This field contains all possible hashes.
     */
    identification_hashes: z.array(z.string()),
});
/**
 * Generated from the `GetSessionResponse` component of the Enable Banking OpenAPI
 * document.
 */
export const ebGetSessionResponseSchema = z.object({
    /**
     * Session status.
     */
    status: ebSessionStatusSchema,
    /**
     * List of account ids available in the session.
     */
    accounts: z.array(z.string()),
    /**
     * Accounts data stored in the session.
     */
    accounts_data: z.array(ebSessionAccountSchema),
    /**
     * ASPSP used with the session.
     */
    aspsp: ebASPSPSchema,
    /**
     * PSU type used with the session.
     */
    psu_type: ebPSUTypeSchema,
    /**
     * Hashed unique identification of a PSU used by the client application. In case PSU ID
     * is not passed by the client application, the hash is calculated based on a random
     * value. The hash also inherits the application ID, so different hashes will be
     * calculated when using the same PSU ID with different applications.
     */
    psu_id_hash: z.string(),
    /**
     * Scope of access requested from ASPSP and confirmed by PSU.
     */
    access: ebAccessSchema,
    /**
     * Date and time when the session was created. Spec format: date-time.
     */
    created: z.string(),
    /**
     * Date and time when the session was authorized. Spec format: date-time.
     */
    authorized: z.string().nullish(),
    /**
     * Date and time when the session was closed. Spec format: date-time.
     */
    closed: z.string().nullish(),
});
/**
 * Generated from the `HalBalances` component of the Enable Banking OpenAPI document.
 */
export const ebHalBalancesSchema = z.object({
    /**
     * List of account balances.
     */
    balances: z.array(ebBalanceResourceSchema),
});
/**
 * Generated from the `PartyIdentification` component of the Enable Banking OpenAPI
 * document.
 */
export const ebPartyIdentificationSchema = z.object({
    /**
     * Name by which a party is known and which is usually used to identify that party.
     */
    name: z.string().nullish(),
    /**
     * Information that locates and identifies a specific address, as defined by postal
     * services.
     */
    postal_address: ebPostalAddressSchema.nullish(),
    /**
     * Unique identification of an account, a person or an organisation, as assigned by an
     * issuer.
     */
    organisation_id: ebGenericIdentificationSchema.nullish(),
    /**
     * Unique identification of an account, a person or an organisation, as assigned by an
     * issuer.
     */
    private_id: ebGenericIdentificationSchema.nullish(),
    /**
     * Specifies the contact details associated with a person or an organisation.
     */
    contact_details: ebContactDetailsSchema.nullish(),
});
/**
 * Generated from the `TransactionStatus` component of the Enable Banking OpenAPI
 * document.
 */
export const ebTransactionStatusSchema = z.enum(["BOOK", "CNCL", "HOLD", "OTHR", "PDNG", "RJCT", "SCHD"]);
/**
 * Generated from the `Transaction` component of the Enable Banking OpenAPI document.
 */
export const ebTransactionSchema = z.object({
    /**
     * Unique transaction identifier provided by ASPSP. This identifier is both unique and
     * immutable for accounts with the same identification hashes and can be used for
     * matching transactions across multiple PSU authentication sessions. Usually the same
     * identifier is available for transactions in ASPSP's online/mobile interface and is
     * called archive ID or similarly. Please note that this identifier is not globally
     * unique and same entry references are likely to occur for transactions belonging to
     * different accounts.
     */
    entry_reference: z.string().nullish(),
    /**
     * Category code conform to ISO 18245, related to the type of services or goods the
     * merchant provides for the transaction.
     */
    merchant_category_code: z.string().nullish(),
    /**
     * Monetary sum of the transaction.
     */
    transaction_amount: ebAmountTypeSchema,
    /**
     * Identification of the party receiving funds in the transaction.
     */
    creditor: ebPartyIdentificationSchema.nullish(),
    /**
     * Identification of the account on which the transaction is credited.
     */
    creditor_account: ebAccountIdentificationSchema.nullish(),
    /**
     * Identification of the creditor agent.
     */
    creditor_agent: ebFinancialInstitutionIdentificationSchema.nullish(),
    /**
     * Identification of the party sending funds in the transaction.
     */
    debtor: ebPartyIdentificationSchema.nullish(),
    /**
     * Identification of the account on which the transaction is debited.
     */
    debtor_account: ebAccountIdentificationSchema.nullish(),
    /**
     * Identification of the debtor agent.
     */
    debtor_agent: ebFinancialInstitutionIdentificationSchema.nullish(),
    /**
     * Allows the account servicer to correctly report a transaction, which in its turn
     * will help account holders to perform their cash management and reconciliation
     * operations.
     */
    bank_transaction_code: ebBankTransactionCodeSchema.nullish(),
    /**
     * Accounting flow of the transaction.
     */
    credit_debit_indicator: ebCreditDebitIndicatorSchema,
    /**
     * Available transaction status values.
     */
    status: ebTransactionStatusSchema,
    /**
     * Booking date of the transaction on the account, i.e. the date at which the
     * transaction has been recorded on books. Spec format: date.
     */
    booking_date: z.string().nullish(),
    /**
     * Value date of the transaction on the account, i.e. the date at which funds become
     * available to the account holder (in case of a credit transaction), or cease to be
     * available to the account holder (in case of a debit transaction). Spec format: date.
     */
    value_date: z.string().nullish(),
    /**
     * Date used for specific purposes: - for card transaction: date of the transaction -
     * for credit transfer: acquiring date of the transaction - for direct debit: receiving
     * date of the transaction. Spec format: date.
     */
    transaction_date: z.string().nullish(),
    /**
     * Funds on the account after execution of the transaction.
     */
    balance_after_transaction: ebAmountTypeSchema.nullish(),
    /**
     * Credit transfer reference number (also known as the creditor reference or the
     * structured creditor reference). The value is set when it is known that the
     * transaction data contains a reference number (in either ISO 11649 or another
     * format). If the format is known it is provided in the reference_number_schema field.
     */
    reference_number: z.string().nullish(),
    /**
     * Indicates what kind of reference number is used.
     */
    reference_number_schema: ebReferenceNumberSchemeSchema.nullish(),
    /**
     * Payment details. For credit transfers may contain free text, reference number or
     * both at the same time (in case Extended Remittance Information is supported). When
     * it is known that remittance information contains a reference number (either based on
     * ISO 11649 or a local scheme), the reference number is also available via the
     * `reference_number` field.
     */
    remittance_information: z.array(z.string()).nullish(),
    /**
     * All other debtor account identifiers provided by ASPSPs.
     */
    debtor_account_additional_identification: z.array(ebGenericIdentificationSchema).nullish(),
    /**
     * All other creditor account identifiers provided by ASPSPs.
     */
    creditor_account_additional_identification: z.array(ebGenericIdentificationSchema).nullish(),
    /**
     * Provides details on the currency exchange rate and contract.
     */
    exchange_rate: ebExchangeRateSchema.nullish(),
    /**
     * The internal note made by PSU.
     */
    note: z.string().nullish(),
    /**
     * Identification used for fetching transaction details.This value can not be used to
     * uniquely identify transactions and may change if the list of transactions is
     * retrieved again. Null if fetching transaction details is not supported.
     */
    transaction_id: z.string().nullish(),
});
/**
 * Generated from the `HalTransactions` component of the Enable Banking OpenAPI
 * document.
 */
export const ebHalTransactionsSchema = z.object({
    /**
     * List of transactions.
     */
    transactions: z.array(ebTransactionSchema),
    /**
     * Value to retrieve next page of transactions. Null if there are no more pages. Only
     * valid in current session.
     */
    continuation_key: z.string().nullish(),
});
/**
 * Generated from the `StartAuthorizationRequest` component of the Enable Banking
 * OpenAPI document.
 */
export const ebStartAuthorizationRequestSchema = z.object({
    /**
     * Scope of access to be request from ASPSP and to be confirmed by PSU.
     */
    access: ebAccessSchema,
    /**
     * ASPSP that PSU is going to be authenticated to.
     */
    aspsp: ebASPSPSchema,
    /**
     * Arbitrary string. Same string will be returned in query parameter when redirecting
     * to the URL passed via redirect_url parameter.
     */
    state: z.string(),
    /**
     * URL that PSU will be redirected to after authorization. Spec format: uri.
     */
    redirect_url: z.string().min(1),
    /**
     * PSU type used for the authorization flow. This parameter affects how the
     * authorization is performed at the ASPSP (e.g., whether a personal or business login
     * page is shown). A mismatch between the provided psu_type and the PSU's actual
     * account type can result in authorization failures or the inability to see expected
     * accounts. Since the default value depends on the specific connector, it is highly
     * recommended to always provide this value for consistent behavior. If the application
     * cannot determine the PSU type, it should be requested from the end user (PSU).
     */
    psu_type: ebPSUTypeSchema.nullish(),
    /**
     * Desired authorization method (in case ASPSP integration supports multiple).
     * Supported methods can be obtained from the `auth_methods` field available in ASPSP
     * details.
     */
    auth_method: z.string().nullish(),
    /**
     * PSU credentials (e.g., user and/or company ID). If not provided through the API,
     * they will be requested from the PSU during authorization. Credentials can be
     * supplied only if `auth_method` is specified; otherwise, a `WRONG_REQUEST_PARAMETERS`
     * error will be returned.
     */
    credentials: z.record(z.string(), z.unknown()).nullish(),
    /**
     * Controls whether user credentials will be autosubmitted (if passed). If set to
     * `false` then credentials form will be prefilled with passed credentials.
     */
    credentials_autosubmit: z.boolean().nullish(),
    /**
     * Preferred PSU language. Two-letter lowercase language code.
     */
    language: z.string().regex(/^[a-z]{2}$/).nullish(),
    /**
     * Unique identification of a PSU used by the client application. It can be used to
     * match sessions of the same user. Although only hashed value is stored, it is
     * recommended to use anonymised identifiers (i.e. digital ID instead of email or
     * social security number). In case the parameter is not passed by the application,
     * random value will be used.
     */
    psu_id: z.string().nullish(),
});
/**
 * Generated from the `StartAuthorizationResponse` component of the Enable Banking
 * OpenAPI document.
 */
export const ebStartAuthorizationResponseSchema = z.object({
    /**
     * URL to redirect PSU to. Spec format: uri.
     */
    url: z.string().min(1),
    /**
     * PSU authorisation ID, a value used to identify an authorisation session. Please note
     * that another session ID will used to fetch account data. Spec format: uuid.
     */
    authorization_id: z.string(),
    /**
     * Hashed unique identification of a PSU used by the client application. In case PSU ID
     * is not passed by the client application, the hash is calculated based on a random
     * value. The hash also inherits the application ID, so different hashes will be
     * calculated when using the same PSU ID with different applications.
     */
    psu_id_hash: z.string(),
});
//# sourceMappingURL=enableBanking.generated.js.map